/** @format */

import * as R from 'ramda';
import Alert from '@mui/material/Alert';
import {Box, IconButton, LinearProgress, Link, Typography} from '@mui/material';
import {Trans} from 'react-i18next';
import {useNavigate, useLocation} from 'react-router-dom';
import AddCircleOutlineRounded from '@mui/icons-material/AddCircleOutlineRounded';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteForeverOutlinedIcon from '@mui/icons-material/DeleteForeverOutlined';
import LinkIcon from '@mui/icons-material/Link';

import Breadcrumbs from 'components/Breadcrumbs';
import PrimaryMediumButton from 'components/buttons/PrimaryMediumButton';

import NotFoundMessage from 'components/NotFoundMessage';
import {useSearchParams, useCallbackOnReturn} from 'helpers/routerHelpers';

import Row from './Row';
import {useStyles} from './styles';
import {
  useDeleteNotificationMutation,
  useDeleteSpecialistMutation,
  useGetCurrentUserListQuery,
} from '../graphql/__generated__';

export const NotificationList = () => {
  const navigate = useNavigate();
  const location = useLocation();

  const searchParams = useSearchParams();
  const specialistId = searchParams.get('specialistId');
  const hideInfo = searchParams.get('hideInfo');

  const classes = useStyles();

  const {loading, error, data, refetch} = useGetCurrentUserListQuery({
    skip: !specialistId,
    variables: {specialistId: specialistId!},
  });
  const [deleteSpecialist, {loading: deleteSpecialistLoading}] = useDeleteSpecialistMutation();
  const [deleteNotification] = useDeleteNotificationMutation();

  useCallbackOnReturn(refetch);

  if (error) {
    if (error.graphQLErrors && error.graphQLErrors.length > 0) {
      if (error.graphQLErrors[0].extensions?.code === 'NOT_FOUND') {
        return <NotFoundMessage />;
      }
    }
    return <Alert severity='error'>{error.message}</Alert>;
  }
  if (loading || !data?.currentUser?.specialist) {
    return <LinearProgress style={{flex: 1}} />;
  }

  const {specialist} = data.currentUser;

  const sortedNotifications = R.sortBy(item => item.company.name, specialist.notifications);

  const handleCreate = () => {
    navigate({
      pathname: '/home/settings/notification',
      search: `?specialistId=${specialistId}`,
    });
  };

  const handleEditSpecialist = () => {
    navigate(
      {
        pathname: '/home/settings/specialist',
        search: `?id=${specialistId}`,
      },
      {state: {background: location}},
    );
  };

  const handleDeleteSpecialist = async () => {
    await deleteSpecialist({variables: {id: specialist.id}});
    navigate('/home/settings/specialists', {replace: true});
  };

  const botLink = `http://t.me/adsensor_notifications_bot?start=${specialist.uuid}`;

  const handleEditNotification = (id: string) => {
    navigate(`/home/settings/notification?specialistId=${specialistId}&id=${id}`);
  };

  const handleDeleteNotification = async (id: string) => {
    await deleteNotification({variables: {id}});
    refetch();
  };

  const breadcrumbs = [
    {
      href: '/home/settings',
      title: <Trans>Настройки</Trans>,
    },
    {
      href: '/home/settings/specialists',
      title: <Trans>Уведомления</Trans>,
    },
    {
      href: '',
      title: <Trans>Управление</Trans>,
    },
  ];

  return (
    <>
      <Box mb={10}>
        <Breadcrumbs items={breadcrumbs} />
      </Box>

      {hideInfo === null && (
        <Box display='flex' justifyContent='space-between' flexWrap={'wrap'}>
          <Box>
            <Typography className={classes.specialistTitle}>{specialist.name}</Typography>

            <Box display='flex' flexDirection='column' mt={3}>
              <Typography variant='body2'>{specialist.email}</Typography>
            </Box>
          </Box>
          <Box className={classes.actionButtonsContainer}>
            <IconButton
              className={classes.actionButton}
              disabled={deleteSpecialistLoading}
              size='small'
              aria-label='edit-button'
              onClick={handleEditSpecialist}
            >
              <EditOutlinedIcon htmlColor='#fff' fontSize='small' />
            </IconButton>

            <IconButton
              className={classes.actionButton}
              disabled={deleteSpecialistLoading}
              size='small'
              aria-label='delete-button'
              onClick={handleDeleteSpecialist}
            >
              <DeleteForeverOutlinedIcon htmlColor='#fff' fontSize='small' />
            </IconButton>
          </Box>
        </Box>
      )}

      <Box
        className={classes.row}
        display={'flex'}
        alignItems={'center'}
        justifyContent={'space-between'}
        flexWrap={'wrap'}
      >
        <Box display='flex' flexDirection='column'>
          <Box>
            <Typography className={classes.tgBotTitle}>
              <Trans>Телеграм бот</Trans>
            </Typography>
          </Box>
          <Box mt={2}>
            <Typography className={classes.hintNew}>
              <Trans>
                Вы можете скопировать и отправить эту ссылку специалисту, который будет получать
                уведомления
              </Trans>
            </Typography>
          </Box>
        </Box>
        <Link className={classes.linkNew} href={botLink} target='_blank'>
          <LinkIcon className={classes.linkIcon} fontSize='small' htmlColor='#fff' />
          <Trans>Ссылка на бота</Trans>
        </Link>
      </Box>

      <Box display='flex' flexDirection='column' mt={3}>
        {sortedNotifications.map((v, i) => (
          <Row
            key={i}
            notification={v}
            onEdit={() => handleEditNotification(v.id)}
            onDelete={() => handleDeleteNotification(v.id)}
          />
        ))}
      </Box>

      <Box mt={5}>
        <PrimaryMediumButton className={classes.addNotificationButton} onClick={handleCreate}>
          <Box mr={2} display='flex' alignItems='center'>
            <AddCircleOutlineRounded fontSize='small' />
          </Box>
          <Trans>Добавить уведомление</Trans>
        </PrimaryMediumButton>
      </Box>
    </>
  );
};
